import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Gauge } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './chartTheme';

function MiniLine({ data, xKey, yKey, xLabel, yLabel, color }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yLabel} vs ${xLabel}`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${yKey}`} />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={data} margin={{ top: 4, right: 12, left: 4, bottom: 20 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey={xKey} type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: xLabel, position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }}
            tickFormatter={(v) => Number(v).toFixed(1)}
          />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={(v) => Number(v).toFixed(1)} />
          <Line type="monotone" dataKey={yKey} stroke={color} {...LINE_PROPS} />
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Vehicle Graphs (§3 / plots 1-3): the three baseline vehicle-level plots.
 * Plot 1 (Speed vs Time) comes from a full-throttle 0-topspeed acceleration trace —
 * the natural time-series pairing (time on X, speed on Y), matching how every other
 * speed-time trace on this site is plotted. Plots 2-3 (Wheel Traction Power / Wheel
 * Torque vs Speed) come from the steady-state flat-road load sweep instead — the power/
 * torque required to sustain each speed on the flat, which is what "baseline" means once
 * plots 4-7 take over the climbing scenarios.
 */
const VehicleGraphsChart = ({ timeS, timeTraceSpeedKph, baselineSweep }) => {
  const timeTrace = useMemo(
    () => (timeS || []).map((t, i) => ({ time_s: t, speed_kph: timeTraceSpeedKph[i] })),
    [timeS, timeTraceSpeedKph],
  );
  if (!timeTrace.length || !baselineSweep || baselineSweep.length === 0) return null;

  return (
    <ChartCard
      icon={Gauge}
      title="Vehicle Graphs"
      description="Baseline vehicle-level performance: acceleration trace and flat-road wheel load vs speed."
      testId="chart-vehicle-graphs"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MiniLine data={timeTrace} xKey="time_s" yKey="speed_kph" xLabel="Time (s)" yLabel="Speed (km/h)" color={SERIES_COLORS[0]} />
        <MiniLine data={baselineSweep} xKey="speed_kph" yKey="wheel_power_kw" xLabel="Speed (km/h)" yLabel="Wheel Traction Power (kW)" color={SERIES_COLORS[1]} />
        <MiniLine data={baselineSweep} xKey="speed_kph" yKey="wheel_torque_nm" xLabel="Speed (km/h)" yLabel="Wheel Torque (Nm)" color={SERIES_COLORS[2]} />
      </div>
    </ChartCard>
  );
};

export default VehicleGraphsChart;
